<template>
  <StepLayout
    :on-save="onSave"
    :on-back="onBack"
    :on-next="canProceedToNextStep ? onNext : undefined"
    :helpscout-article="csvProcessing || csvFailedToProcess || fetchingInitialResults ? null : '656891b6b1d9b5438dbdb554'"
    step-navigation-button-data-ec="bulk_send_review_data_page"
  >
    <template v-slot:icon>
      <SpinLoader class="mr-sm" size="small" v-if="csvProcessing || fetchingInitialResults" />
      <!-- TODO replace icon with component that has been created in Step 12-->
      <ExclamationIcon
        v-if="csvFailedToProcess || noData"
        class="text-white bg-envelopeStatus-cancelled rounded-full h-base w-base p-xs mr-sm"
      />
    </template>
    <template v-slot:title>
      <template>
        {{ stepTitle }}
      </template>
    </template>

    <div v-if="csvProcessing || fetchingInitialResults">
      <TextSkeleton :pulse="true" class="block w-full h-thirtySix mb-base" />
      <TextSkeleton :pulse="true" class="block w-full h-thirtySix" />
    </div>
    <div v-else-if="csvFailedToProcess">
      <i18n path="bulkSend.steps.reviewData.importHasFailed" tag="p" class="mb-twenty">
        <template v-slot:link>
          <RouterLink :to="prevRoute">{{ $t('bulkSend.steps.reviewData.returnToPreviousStep') }}</RouterLink>
        </template>
      </i18n>
    </div>
    <div v-else-if="noData">
      <i18n path="bulkSend.steps.reviewData.noData" tag="p" class="mb-twenty">
        <template v-slot:link>
          <RouterLink :to="prevRoute">{{ $t('bulkSend.steps.reviewData.returnToPreviousStep') }}</RouterLink>
        </template>
      </i18n>
    </div>
    <div v-else>

      <InvalidBulkSendRows
        v-if="invalidRowList.length > 0"
        :results="invalidRowList"
        :meta="invalidMeta"
        @page="handleInvalidPagination"
      />

      <hr v-if="showDivider" class="pb-sm">

      <ValidBulkSendRows
        v-if="validRowList.length > 0"
        :results="validRowList"
        :meta="validMeta"
        @page="handleValidPagination"
      />
    </div>
  </StepLayout>
</template>

<script>
// @ts-check
import { computed, defineComponent, onActivated, onDeactivated, ref } from 'vue';
import { useActions, useGetters } from 'vuex-composition-helpers/dist';

import callUntil from '@/utils/callUntil';
import useRequest from '@/composables/actions/request';
import useBulkSendRoutes from '@/composables/bulkSend/routes';
import { useBulkSendStepsNavigation } from '@/composables/bulkSend/steps';
import useList from '@/composables/list/list';
import ImportedRowsCsvData from '@/store/modules/bulkSend/models/ImportedRowsCsvData';
import MetaImportedRowsCsv from '@/store/modules/bulkSend/models/MetaImportedRowsCsv';

import SpinLoader from '@/components/library/loaders/SpinLoader.vue';
import TextSkeleton from '@/components/library/skeletons/TextSkeleton.vue';
import StepLayout from '@/components/ui/bulkSend/StepLayout.vue';
import InvalidBulkSendRows from '@/components/ui/bulkSend/reviewData/InvalidBulkSendRows.vue';
import ValidBulkSendRows from '@/components/ui/bulkSend/reviewData/ValidBulkSendRows.vue';
import { ExclamationIcon } from '@/components/icons/fontIcons';

export default defineComponent({
  name: 'ReviewDataPage',
  components: {
    InvalidBulkSendRows,
    SpinLoader,
    ValidBulkSendRows,
    StepLayout,
    TextSkeleton,
    ExclamationIcon,
  },
  setup() {
    // @ts-check
    const { bulkSend } = useGetters({ bulkSend: 'bulkSend/bulkSend' });
    const { fetchTheBulkSend } = useActions({
      fetchTheBulkSend: 'bulkSend/fetchTheBulkSend',
    });

    const downloadErrorsRequest = useRequest('bulk-sends', {
      endpoint: 'imported-rows/csv-data/download-errors',
      method: 'GET',
      responseType: 'blob',
    });

    const { prevRoute } = useBulkSendRoutes();
    const { onNext, onSave, onBack } = useBulkSendStepsNavigation();

    const fetchingInitialResults = ref(false);
    const abortController = ref();

    const [
      fetchValidRows,
      validRowList,
      validMeta,
      validIsLoading,
    ] = useList(
      `bulk-sends/${bulkSend.value.fingerprint}/imported-rows/csv-data`,
      ImportedRowsCsvData,
      MetaImportedRowsCsv,
      {
        per_page: 10,
        only_valid: 1,
      },
    );

    const [
      fetchInvalidRows,
      invalidRowList,
      invalidMeta,
      invalidIsLoading,
    ] = useList(
      `bulk-sends/${bulkSend.value.fingerprint}/imported-rows/csv-data`,
      ImportedRowsCsvData,
      MetaImportedRowsCsv,
      {
        per_page: 10,
        only_invalid: 1,
      },
    );

    const csvProcessing = computed(() => ['queued', 'processing'].includes(bulkSend.value.import_status));

    const checkImportStatus = async () => {
      if (!bulkSend.value.fingerprint) {
        return true;
      }

      await fetchTheBulkSend(bulkSend.value.fingerprint);

      return !csvProcessing.value;
    };

    onActivated(async () => {
      abortController.value = new AbortController();
      fetchingInitialResults.value = true;

      try {
        if (csvProcessing.value) {
          await callUntil(checkImportStatus, { signal: abortController.value.signal });
        }

        await Promise.all([
          fetchInvalidRows(),
          fetchValidRows(),
        ]);
      } catch (error) {
        if (error.message !== 'Aborted') {
          throw (error);
        }
      } finally {
        fetchingInitialResults.value = false;
      }
    });

    onDeactivated(() => abortController.value.abort());

    return {
      bulkSend,
      fetchingInitialResults,
      fetchValidRows,
      validRowList,
      validMeta,
      validIsLoading,
      fetchInvalidRows,
      invalidRowList,
      invalidMeta,
      invalidIsLoading,
      downloadErrorsRequest,
      prevRoute,
      onBack,
      onNext,
      onSave,
      csvProcessing,
    };
  },
  computed: {
    stepTitle() {
      if (this.csvProcessing) {
        return this.$t('bulkSend.steps.reviewData.titleProcessing');
      }

      if (this.fetchingInitialResults) {
        return this.$t('bulkSend.steps.reviewData.titleFetching');
      }

      if (this.csvFailedToProcess) {
        return this.$t('bulkSend.steps.reviewData.titleCsvFailed', { name: this.bulkSend.name });
      }

      if (this.noData) {
        return this.$t('bulkSend.steps.reviewData.titleNoData', { name: this.bulkSend.name });
      }

      return this.$t('bulkSend.steps.reviewData.title', { name: this.bulkSend.name });
    },
    csvFailedToProcess() {
      return this.bulkSend.import_status === 'failed';
    },
    canProceedToNextStep() {
      return !this.fetchingInitialResults && this.validRowList.length > 0 && this.invalidRowList.length === 0 && !this.csvProcessing;
    },
    showDivider() {
      return !this.fetchingInitialResults && this.invalidRowList.length > 0 && this.validRowList.length > 0;
    },
    noData() {
      return !this.fetchingInitialResults && this.validRowList.length === 0 && this.invalidRowList.length === 0;
    },
  },
  methods: {
    handleInvalidPagination(url) {
      const parsedUrl = new URL(url);

      this.fetchInvalidRows({
        page: parsedUrl.searchParams.get('page'),
      });
    },
    handleValidPagination(url) {
      const parsedUrl = new URL(url);

      this.fetchValidRows({
        page: parsedUrl.searchParams.get('page'),
      });
    },
  },
});
</script>
